import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle, Button, DataTable, Skeleton, StatCard, TablePill, type DataTableColumn } from "@akuna/ui";
import { Download } from "lucide-react";
import { hexToRgba } from "@/components/calendar/views/calendarEventUtils";
import type { AppointmentRow, ExplorerFormat, ExplorerSaleItem, ItemRow, ItemTypeValue, SalesExplorerResult } from "./types";

interface Props {
  error: Error | null;
  exportDisabled: boolean;
  onExport: () => void;
  /** Formato previsto pelos filtros, usado só enquanto a resposta não chega.
   *  A autoridade é o `format` que a RPC devolve. */
  expectedFormat: ExplorerFormat;
  loading: boolean;
  onPageChange: (page: number) => void;
  page: number;
  pageSize: number;
  result: SalesExplorerResult | undefined;
}

/** Mesma cor de fallback do Flowboard, para um tipo sem cor cadastrada. */
const DEFAULT_TYPE_COLOR = "#6366f1";

/** Mesma receita de `typeSoftStyle` em flowboardListColumns.tsx: texto na cor
 *  do tipo do agendamento, fundo na mesma cor com 14% de opacidade — a cor
 *  vem de clinic_appointment_types.color, a mesma que o Flowboard usa. */
const typeSoftStyle = (color: string | null) => {
  const resolved = color ?? DEFAULT_TYPE_COLOR;
  return { backgroundColor: hexToRgba(resolved, 0.14), color: resolved };
};

const currency = (value: number, locale: string) =>
  new Intl.NumberFormat(locale, { currency: "BRL", style: "currency" }).format(value);

const dateOnly = (value: string, locale: string) => new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(new Date(value));

function itemTypeLabel(t: (key: string) => string, type: ItemTypeValue | null): string {
  if (type === "PRODUTO") return t("salesExplorerItemTypeProduct");
  if (type === "SERVICO") return t("salesExplorerItemTypeService");
  if (type === "PACOTE") return t("salesExplorerItemTypePackage");
  return "—";
}

function PetLink({ id, name }: { id: string | null; name: string | null }) {
  if (!name) return <span className="text-muted-foreground">—</span>;
  if (!id) return <span className="truncate font-semibold text-foreground">{name}</span>;

  return (
    <Link className="truncate font-semibold text-primary hover:underline" to={`/patients/pets/${id}`}>
      {name}
    </Link>
  );
}

function OwnerLink({ id, name }: { id: string | null; name: string | null }) {
  if (!name) return <span className="text-muted-foreground">—</span>;
  if (!id) return <span className="truncate text-foreground">{name}</span>;

  return (
    <Link className="truncate text-primary hover:underline" to={`/patients/owners/${id}`}>
      {name}
    </Link>
  );
}

/**
 * Todos os itens da venda direto na linha — sem truncar nome nenhum e sem
 * cortar em "+N". A célula do DataTable tem altura fixa (linha de tabela),
 * então em vez de limitar quantidade a lista quebra em várias linhas
 * (flex-wrap) dentro de uma área que rola verticalmente quando não cabe.
 * Cor neutra (cinza/preto): a distinção "casou com o filtro de item" fica só
 * na linha expandida, aqui é só "o que tem na venda".
 */
function InlineItems({ items, noItemsLabel, typeLabel }: { items: ExplorerSaleItem[]; noItemsLabel: string; typeLabel: (t: ItemTypeValue | null) => string }) {
  if (items.length === 0) return <span className="text-muted-foreground">{noItemsLabel}</span>;

  return (
    <div className="flex h-auto max-h-full min-h-full w-full flex-wrap items-center justify-start gap-1 overflow-y-auto py-2">
      {items.map((item) => (
        <span
          className="inline-flex items-center gap-1 rounded-full bg-orange-50 px-2 py-0.5 text-xs text-foreground"
          key={item.saleItemId}
        >
          {item.itemName}
          {item.quantity > 1 ? <span className="text-muted-foreground">×{item.quantity}</span> : null}
        </span>
      ))}
    </div>
  );
}

export function SalesExplorerResults({ error, expectedFormat, exportDisabled, loading, onExport, onPageChange, page, pageSize, result }: Props) {
  const { i18n, t } = useTranslation("reports");
  const locale = i18n.language;

  if (error) {
    return (
      <Alert variant="danger">
        <AlertTitle>{t("salesExplorerErrorTitle")}</AlertTitle>
        <AlertDescription>{error.message}</AlertDescription>
      </Alert>
    );
  }

  const format = result?.format ?? expectedFormat;

  const countLabel = result
    ? format === "item"
      ? t("salesExplorerCountItems", { count: result.totalQuantity })
      : t("salesExplorerCountAppointments", { count: result.totalCount })
    : "—";

  const appointmentColumns: DataTableColumn<AppointmentRow>[] = [
    {
      cell: (row) => dateOnly(row.occurredAt, locale),
      header: t("salesExplorerColDate"),
      id: "date",
      minWidth: 150,
      sortAccessor: (row) => row.occurredAt,
      width: 170,
    },
    {
      cell: (row) => <PetLink id={row.petId} name={row.petName} />,
      header: t("salesExplorerColPet"),
      id: "pet",
      minWidth: 100,
      sortAccessor: (row) => (row.petName ?? "").toLowerCase(),
      width: 140,
    },
    {
      cell: (row) => <OwnerLink id={row.ownerId} name={row.ownerName} />,
      header: t("salesExplorerColOwner"),
      id: "owner",
      minWidth: 120,
      sortAccessor: (row) => (row.ownerName ?? "").toLowerCase(),
      width: 170,
    },
    {
      cell: (row) =>
        row.appointmentType ? (
          <TablePill style={typeSoftStyle(row.appointmentTypeColor)}>{row.appointmentType}</TablePill>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
      header: t("salesExplorerColAppointmentType"),
      id: "type",
      minWidth: 110,
      sortAccessor: (row) => (row.appointmentType ?? "").toLowerCase(),
      width: 140,
    },
    {
      cell: (row) => <InlineItems items={row.items} noItemsLabel={t("salesExplorerNoItems")} typeLabel={(type) => itemTypeLabel(t, type)} />,
      grow: true,
      header: t("salesExplorerItemsInline"),
      id: "items",
      minWidth: 220,
      width: 280,
    },
    {
      align: "right",
      cell: (row) => <span className="font-semibold tabular-nums">{currency(row.value, locale)}</span>,
      header: t("salesExplorerColValue"),
      id: "value",
      minWidth: 110,
      sortAccessor: (row) => row.value,
      width: 130,
    },
  ];

  const itemColumns: DataTableColumn<ItemRow>[] = [
    {
      cell: (row) => dateOnly(row.occurredAt, locale),
      header: t("salesExplorerColDate"),
      id: "date",
      minWidth: 150,
      sortAccessor: (row) => row.occurredAt,
      width: 170,
    },
    {
      cell: (row) => <span className="truncate font-semibold text-foreground">{row.itemName}</span>,
      grow: true,
      header: t("salesExplorerColItem"),
      id: "item",
      minWidth: 160,
      sortAccessor: (row) => row.itemName.toLowerCase(),
      width: 220,
    },
    {
      cell: (row) => <span className="text-muted-foreground">{itemTypeLabel(t, row.itemType)}</span>,
      header: t("salesExplorerColItemType"),
      id: "itemType",
      minWidth: 100,
      sortAccessor: (row) => itemTypeLabel(t, row.itemType),
      width: 120,
    },
    {
      cell: (row) => <PetLink id={row.petId} name={row.petName} />,
      header: t("salesExplorerColPet"),
      id: "pet",
      minWidth: 100,
      sortAccessor: (row) => (row.petName ?? "").toLowerCase(),
      width: 140,
    },
    {
      cell: (row) => <OwnerLink id={row.ownerId} name={row.ownerName} />,
      header: t("salesExplorerColOwner"),
      id: "owner",
      minWidth: 120,
      sortAccessor: (row) => (row.ownerName ?? "").toLowerCase(),
      width: 170,
    },
    {
      align: "right",
      cell: (row) => <span className="tabular-nums text-foreground">{row.quantity}</span>,
      header: t("salesExplorerColQuantity"),
      id: "quantity",
      minWidth: 70,
      sortAccessor: (row) => row.quantity,
      width: 90,
    },
    {
      align: "right",
      cell: (row) => <span className="tabular-nums text-foreground">{currency(row.unitPrice, locale)}</span>,
      header: t("salesExplorerColUnitPrice"),
      id: "unitPrice",
      minWidth: 100,
      sortAccessor: (row) => row.unitPrice,
      width: 130,
    },
    {
      align: "right",
      cell: (row) => <span className="font-semibold tabular-nums">{currency(row.lineTotal, locale)}</span>,
      header: t("salesExplorerColTotal"),
      id: "total",
      minWidth: 110,
      sortAccessor: (row) => row.lineTotal,
      width: 130,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="font-display text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
        {t("salesExplorerResultsTitle")}
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label={t("salesExplorerCount")} value={countLabel} />
        <StatCard label={t("salesExplorerTotalValue")} value={result ? currency(result.totalValue, locale) : "—"} />
        <StatCard label={t("salesExplorerAverageValue")} value={result ? currency(result.averageValue, locale) : "—"} />
      </div>

      <div className="flex justify-end">
        <Button disabled={exportDisabled} onClick={onExport} size="sm" variant="outline">
          <Download className="mr-1 h-4 w-4" />
          {t("salesExplorerExport")}
        </Button>
      </div>

      {loading ? (
        <Skeleton label={t("salesExplorerLoading")} />
      ) : result?.format === "item" ? (
        <DataTable
          columns={itemColumns}
          data={result.rows}
          empty={t("salesExplorerEmptyTitle")}
          getRowId={(row) => row.id}
          hideHorizontalScroll
          onPageChange={onPageChange}
          page={page}
          pageSize={pageSize}
          totalItems={result.totalCount}
        />
      ) : (
        <DataTable
          columns={appointmentColumns}
          data={(result?.rows ?? []) as AppointmentRow[]}
          empty={t("salesExplorerEmptyTitle")}
          expandLabel={t("salesExplorerExpand")}
          getRowId={(row) => row.id}
          hideHorizontalScroll
          isRowExpandable={(row) => row.items.length > 0}
          onPageChange={onPageChange}
          page={page}
          pageSize={pageSize}
          renderExpandedRow={(row) => (
            <table className="w-full max-w-3xl text-sm">
              <thead>
                <tr className="text-xs uppercase tracking-wide text-muted-foreground">
                  <th className="py-1 text-left font-semibold">{t("salesExplorerColItem")}</th>
                  <th className="py-1 text-left font-semibold">{t("salesExplorerColItemType")}</th>
                  <th className="py-1 text-right font-semibold">{t("salesExplorerColQuantity")}</th>
                  <th className="py-1 text-right font-semibold">{t("salesExplorerColUnitPrice")}</th>
                  <th className="py-1 text-right font-semibold">{t("salesExplorerColTotal")}</th>
                </tr>
              </thead>
              <tbody>
                {row.items.map((item) => (
                  <tr key={item.saleItemId}>
                    <td className="py-1 font-semibold text-foreground">{item.itemName}</td>
                    <td className="py-1 text-muted-foreground">{itemTypeLabel(t, item.itemType)}</td>
                    <td className="py-1 text-right tabular-nums text-foreground">{item.quantity}</td>
                    <td className="py-1 text-right tabular-nums text-foreground">{currency(item.unitPrice, locale)}</td>
                    <td className="py-1 text-right tabular-nums text-foreground">{currency(item.lineTotal, locale)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          totalItems={result?.totalCount ?? 0}
        />
      )}

    </div>
  );
}
